import React from "react";
import { toast } from "sonner";
import { BellRing, Link2Off, MessageCircle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { residentApi, errMsg } from "@/lib/api";
import { inr } from "@/lib/format";
import { Card } from "@/components/common";
import { useResidentApi } from "@/pages/resident/useResidentApi";

export function shareText(rooms: any[], url: string) {
  const lines = rooms.slice(0, 8).map((r) => `• ${r.pg_name} – Room ${r.name} – ${inr(r.rent_per_bed)}/bed${r.vacant_beds ? "" : " (full)"}`);
  return `Help me choose a PG! Here are the rooms I've shortlisted on Nexstays:\n${lines.join("\n")}${rooms.length > 8 ? `\n…and ${rooms.length - 8} more` : ""}\n\nCompare them here: ${url}`;
}

export function SavedTools({ rooms }: { rooms: any[] }) {
  const { data: s, reload } = useResidentApi<{ alerts: boolean; share_id: string | null }>("/resident/shortlist/settings");
  const setAlerts = async (alerts: boolean) => {
    try { await residentApi.patch("/resident/shortlist/settings", { alerts }); reload(); toast.success(alerts ? "Free-bed emails on" : "Free-bed emails off"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const share = async () => {
    const tab = window.open("", "_blank");
    try {
      const { data } = await residentApi.post("/resident/shortlist/share");
      const url = `https://wa.me/?text=${encodeURIComponent(shareText(rooms, `${window.location.origin}/s/${data.share_id}`))}`;
      if (tab) tab.location.href = url; else window.location.href = url;
      reload();
    } catch (e) { tab?.close(); toast.error(errMsg(e)); }
  };
  const stop = async () => {
    if (!window.confirm("Turn off your shared link? People you sent it to won't be able to open it.")) return;
    try { await residentApi.delete("/resident/shortlist/share"); reload(); toast.success("Shared link turned off"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card className="p-4 mb-3 space-y-3" data-testid="saved-tools">
      <label className="flex items-center justify-between gap-3 cursor-pointer">
        <span className="flex items-start gap-2.5 min-w-0">
          <BellRing className="h-5 w-5 text-[#3d52a0] shrink-0 mt-0.5" />
          <span><span className="block text-sm font-semibold">Email me when a saved room frees up</span>
            <span className="block text-xs text-slate-500">We'll tell you as soon as a full room gets a free bed.</span></span>
        </span>
        <Switch data-testid="saved-alerts-switch" checked={s ? s.alerts : true} disabled={!s} onCheckedChange={setAlerts} />
      </label>
      <button data-testid="saved-share-btn" onClick={share} className="w-full h-12 rounded-xl bg-[#1f8f4e] hover:bg-[#187540] text-white font-semibold text-sm flex items-center justify-center gap-2 pg-tap">
        <MessageCircle className="h-5 w-5" />Share on WhatsApp
      </button>
      {s?.share_id && (
        <div className="flex items-center justify-between gap-2 text-xs text-slate-500" data-testid="saved-share-active">
          <span>Your shared link is on. Anyone with it can view these rooms.</span>
          <button data-testid="saved-share-stop-btn" onClick={stop} className="shrink-0 font-semibold text-rose-600 flex items-center gap-1"><Link2Off className="h-3.5 w-3.5" />Turn off</button>
        </div>
      )}
    </Card>
  );
}
